import { DataMapper } from './transport';
import { IDropdownOption } from '../domain/entities/Config';
import { ADDRESS_TYPE, INPUT_TYPE } from '../constants/Constants';
import { IFromGetJsonAddressesSchemaResponse } from './IDataMappings';
import { IAddressAttributes } from '../domain/entities/Address';

/* eslint-disable @typescript-eslint/naming-convention */

export interface ICountryCallingCodeConfig {
  country: string;
  countryCallingCode: string;
  pattern: string; 
  formatter?: string;
}

export interface IPhonePattern {
  [country_code: string]: ICountryCallingCodeConfig;
}

export interface IAddressDisplaySchemaProperty {
  x: number;
  y: number;
  field_separator?: string;
  mask?: string;
  asNational?: boolean;
}

export interface IOneOf {
  pattern: string;
  label: string;
}

export type IStaticLookupName = 'stateCityUrl' | 'stateCityLockerUrl';

export type AutoChangePhoneNumberPropertyType = {
  pattern: string;
  replacement: string;
};

export interface ISchemaAdditionalProperties {
  autochangePhoneNumber?: AutoChangePhoneNumberPropertyType;
  excludeCountryCode?: number;
  phoneNumberFormat?: object;
  show_flag?: number;
  autofill?: string;
  depends_on_field?: keyof IAddressAttributes;
  x: number;
  y: number;
  widget?: string;
  labelKey?: string;
  input_type?: INPUT_TYPE;
  display?: IAddressDisplaySchemaProperty;
  staticLookupName?: IStaticLookupName;
  r?: string; // property used for api resource of that field.
  mask?: string;
  response_key?: string;
  countryCodes?: ICountryCallingCodeConfig[];
  address_auto_fill_fields?: Array<keyof IAddressAttributes>; // The ones that get auto completed
  address_suggestion_fields?: Array<keyof IAddressAttributes>; // The ones we see in the dropdown
  readonly_field?: number;
  prefix?: string;
  validate_existence?: number;
  options?: IDropdownOption[];
}

export interface IAddressSchemaProperty {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  type?: string;
  required?: number;
  title?: string;
  additionalProperties?: ISchemaAdditionalProperties;
  oneOf?: IOneOf[];
  default?: string;
}

export type IAddressSchemaProperties = Partial<Record<keyof IAddressAttributes, IAddressSchemaProperty>>;

export interface IAddressSchema {
  properties: IAddressSchemaProperties;
  required: Array<keyof IAddressAttributes>;
}

export interface IAddressSchemaGroupDependencies {
  [key: string]: string[];
}

export interface IAddressSchemaGroup {
  type?: string;
  required?: Array<keyof IAddressAttributes>;
  dependencies?: IAddressSchemaGroupDependencies;
  properties?: IAddressSchemaProperties;
}

export interface IAddressSchemaNode extends IAddressSchemaGroup {
  allOf?: IAddressSchemaGroup[];
}

export interface IJsonSchemaProperties {
  addressInputSchema?: IAddressSchemaNode;
}

export interface IJsonSchema {
  $schema: string;
  title: string;
  description: string;
  type: string;
  properties: IJsonSchemaProperties;
}

export type IJsonSchemaAddressType =
  ADDRESS_TYPE.SHIPPING |
  ADDRESS_TYPE.BILLING |
  ADDRESS_TYPE.PACKSTATION;

export interface IGetAddressSchemaParams {
  brandId: string;
  regionId: string;
  locale: string;
  countryId: string;
  addressType: IJsonSchemaAddressType;
}

/* eslint-enable @typescript-eslint/naming-convention */

class JsonSchemaAPI {
  private dataMapper: DataMapper;

  private baseUrl: string;

  constructor(
    dataMapper: DataMapper,
    baseUrl: string,
  ) {
    this.dataMapper = dataMapper;
    this.baseUrl = baseUrl;
  }

  public async getAddressSchema({
    addressType,
    brandId,
    countryId,
    locale,
    regionId,
  }: IGetAddressSchemaParams): Promise<IFromGetJsonAddressesSchemaResponse> {
    const mapAddressTypeToSchemaFolderName: Record<IJsonSchemaAddressType, string> = {
      [ADDRESS_TYPE.SHIPPING]: 'shipping',
      [ADDRESS_TYPE.BILLING]: 'billing',
      [ADDRESS_TYPE.PACKSTATION]: 'packstation',
    };
    const folder = mapAddressTypeToSchemaFolderName[addressType] || '';

    const fileName = `${brandId}-${regionId}-${locale}-${countryId}.json`;

    const response = await fetch(`${this.baseUrl}/lcl/json/${folder}/${fileName}`);
    const json = await response.json() as IJsonSchema;

    return this.dataMapper.mapResponse(
      'addressSchemaMapper',
      json.properties.addressInputSchema,
    ) as IFromGetJsonAddressesSchemaResponse;
  }
}

export default JsonSchemaAPI;
